import { createFileRoute } from "@tanstack/react-router";
import {
  LearnArticleLayout,
  learnArticleHead,
} from "@/components/LearnArticleLayout";

const PATH = "/learn/why-is-my-colorado-business-not-ranking";
const TITLE =
  "Why Is My Colorado Business Not Ranking on Google? 9 Real Reasons";
const DESCRIPTION =
  "The 9 most common reasons Colorado businesses don't rank on Google — from GBP misconfig to NAP inconsistency to thin content. Self-diagnose in 15 minutes.";

export const Route = createFileRoute("/learn/why-is-my-colorado-business-not-ranking")({
  head: () => learnArticleHead({ title: TITLE, description: DESCRIPTION, path: PATH }),
  component: Article,
});

function Article() {
  return (
    <LearnArticleLayout
      category="SEO"
      title={TITLE}
      intro="If you're a Colorado business owner Googling your own service + city and seeing competitors everywhere except your name, one of these nine problems is almost certainly why. Most are fixable in days, not months."
      readTime="7 min read"
      publishedDate="2026-05-29"
      path={PATH}
      definition={{
        term: "Why isn't my business ranking on Google?",
        body: "The most common reasons Colorado businesses don't rank are: an unverified or misconfigured Google Business Profile, NAP inconsistencies across the web, no real city-specific content, slow mobile load, thin or duplicate pages, missing schema, no real reviews, technical crawl blocks, or competing in a saturated market without enough budget to break through.",
      }}
      toc={[
        { id: "gbp", label: "1. Google Business Profile issues" },
        { id: "nap", label: "2. NAP inconsistency" },
        { id: "content", label: "3. No real local content" },
        { id: "speed", label: "4. Slow mobile load" },
        { id: "thin", label: "5. Thin or duplicate pages" },
        { id: "schema", label: "6. Missing schema" },
        { id: "reviews", label: "7. Not enough real reviews" },
        { id: "technical", label: "8. Technical crawl blocks" },
        { id: "competitive", label: "9. Wrong market fit" },
      ]}
      faqs={[
        {
          q: "How do I check if Google can actually crawl my site?",
          a: "Use Google Search Console's URL Inspection tool. Paste your homepage URL — if it shows 'URL is not on Google' or 'Blocked by robots.txt', you've found your problem. Fix the block and request reindexing.",
        },
        {
          q: "How much do reviews matter for ranking in Colorado?",
          a: "A lot. In competitive Colorado markets (Denver HVAC, Springs roofing, Boulder legal), a business with 40 reviews at 4.8 stars will almost always outrank a business with 5 reviews at 5.0 stars — assuming everything else is comparable.",
        },
        {
          q: "Can I rank without spending money on SEO?",
          a: "In a tight geographic niche with little competition (a town under 5,000 people, a rare service category), yes — a properly built GBP plus a clean website is often enough. In any competitive Front Range market, no.",
        },
        {
          q: "Why does my competitor outrank me when their site is worse?",
          a: "Usually because they have more reviews, older domain age, more backlinks, or a stronger Google Business Profile signal. Site quality is one of many ranking factors — and not the heaviest one for local search.",
        },
        {
          q: "How long until I see ranking improvements after fixing these issues?",
          a: "Most fixes show movement within 2–6 weeks. The exceptions are NAP cleanup and review velocity, which compound over 90+ days.",
        },
      ]}
    >
      <h2 id="gbp">1. Google Business Profile issues</h2>
      <p>
        90% of ranking problems for Colorado local businesses trace back to
        the Google Business Profile. Check for:
      </p>
      <ul>
        <li>Unverified profile (look for "Verify now" in the dashboard).</li>
        <li>Wrong primary category (an HVAC company set to "Heating contractor" instead of "HVAC contractor" will get throttled).</li>
        <li>Missing or wrong service-area polygons.</li>
        <li>Hours that don't match your actual operation.</li>
        <li>Suspended profile (you'd see a notice — but many owners never log in to find out).</li>
      </ul>

      <h2 id="nap">2. NAP inconsistency</h2>
      <p>
        Name, Address, Phone — these must match exactly across your website,
        Google Business Profile, Yelp, Facebook, BBB, and the top 50 Colorado
        directories. "Suite 200" vs "Ste 200" vs "#200" counts as
        inconsistent to Google's local algorithm. Use a citation audit tool
        or pull your top 20 listings manually and confirm they match.
      </p>

      <h2 id="content">3. No real local content</h2>
      <p>
        A homepage that says "Serving Colorado" is not local content. Google
        wants pages that prove you actually serve a specific city. If you
        don't have:
      </p>
      <ul>
        <li>A dedicated page per city you serve (Colorado Springs, Monument, Falcon, etc.).</li>
        <li>Neighborhood references inside that content (Briargate, Powers Corridor, Old Colorado City).</li>
        <li>Local landmarks, weather patterns, or Colorado-specific context.</li>
      </ul>
      <p>
        Then you'll get beat by every competitor who does — regardless of
        how good your homepage looks.
      </p>

      <h2 id="speed">4. Slow mobile load</h2>
      <p>
        Run your homepage through PageSpeed Insights. If your mobile score
        is under 50 or your Largest Contentful Paint is over 4 seconds, you
        have a ranking ceiling you can't push past. Common culprits:
        unoptimized hero images, WordPress page-builder bloat, third-party
        chat widgets, video backgrounds.
      </p>

      <h2 id="thin">5. Thin or duplicate pages</h2>
      <p>
        Service pages with 150 words of generic copy don't rank in 2026.
        Neither do "city pages" that are identical except for a find-replace
        on the city name. Google's helpful-content systems demote both. Each
        page needs to be genuinely about that city or service — at least
        600–1,000 words of real, non-templated content.
      </p>

      <h2 id="schema">6. Missing schema</h2>
      <p>
        LocalBusiness, Service, Review, and FAQPage schema are not optional
        for competitive local rankings. They're how Google parses what your
        page is about. Test your homepage in Google's Rich Results Test —
        if it shows zero structured data, that's a fixable problem worth
        addressing this week.
      </p>

      <h2 id="reviews">7. Not enough real reviews</h2>
      <p>
        In Denver, Colorado Springs, and Boulder, the top 3 Map Pack
        businesses almost always have 40+ Google reviews at a 4.5+ star
        average. If you have 8 reviews, your ceiling is page 2 of the Map
        Pack at best. Build a real review request workflow tied to job
        completion or invoicing — not occasional asks.
      </p>

      <h2 id="technical">8. Technical crawl blocks</h2>
      <p>
        Surprisingly common: a stale robots.txt blocking Googlebot, a
        noindex tag left over from staging, a broken sitemap, or
        canonical tags pointing to the wrong URL. Check Google Search
        Console's Coverage report — every "Excluded" reason is a clue.
      </p>

      <h2 id="competitive">9. Wrong market fit</h2>
      <p>
        Sometimes the answer is uncomfortable: you're trying to compete in
        a market where the top 3 spots are held by established competitors
        with 10-year-old domains, 200+ reviews, and a $5,000/month SEO
        budget. You can still win — but not by spending $400/month.
        Either narrow your geographic focus (one suburb instead of all of
        Denver) or budget appropriately for the market you actually want
        to compete in.
      </p>
      <p>
        Work through these nine in order. Most Colorado businesses fix
        problems #1, #2, and #5 and see real ranking movement within 60
        days — without spending another dollar on advertising.
      </p>
    </LearnArticleLayout>
  );
}
